import { Button, Divider } from '@design-system';
import {
  TrackArtistClicked,
  TrackArtistPageView,
  TrackLinkClicked,
} from '@utils/analytics';
import { AnalyticsObjectProvider } from '@utils/analytics/object';
import { updateQueryParameters } from '@utils/urlHelpers';
import { motion } from 'framer-motion';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useContext, useEffect, useMemo, useRef } from 'react';

import { ARTIST_PAGE_TYPE, PLACE_HOLDER_IMAGE } from '../../constants';
import { AuthEntryPoint } from '../../features/auth/types';
import { pathFor } from '../../helpers/links';
import { useIsSubscribed } from '../../hooks/subscription';
import { useAuthenticate } from '../../hooks/useAuthenticate';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { useAuth } from '../../providers';
import { AccountContext } from '../../providers/AccountProvider';
import { ArtistPageData, AugmentedArtistCard } from '../../types/Artists';
import { GeneratedSubscriptionType } from '../../types/generated';
import {
  ArtistStoryblok,
  AssetStoryblok,
  EventStoryblok,
  SocialLinksStoryblok,
  SocialLinkStoryblok,
  VideoStoryblok,
} from '../../types/generated-storyblok';
import { ButtonLocation } from '../../types/Subscriptions';
import { ArtistHero } from '../ArtistV2/ArtistHero/ArtistHero';
import { ArtistAboutTab } from '../ArtistV2/Tabs/ArtistAboutTab';
import { ArtistInsightsTab } from '../ArtistV2/Tabs/ArtistInsightsTab';
import { ArtistProductsTab } from '../ArtistV2/Tabs/ArtistProductsTab';
import StickyHeader from '../Containers/StickyHeader';
import RelatedArtists from '../Content/RelatedArtists';
import { Image } from '../Image';
import PageSection from '../Layout/PageSection';
import ScrollAnchor from '../ScrollAnchor';
import Tabs from '../Tabs';
import { showPhoneStep } from '../../helpers/phone';

type Props = {
  blok: ArtistStoryblok & {
    relatedArtists: AugmentedArtistCard[];
    artistsCount: number;
    internalId: string | null;
  };
  pageData: ArtistPageData;
};

function mapArtistsSocials(links: SocialLinksStoryblok[]) {
  return links.reduce<
    {
      id: string;
      label: string;
      source: string;
      url: string;
    }[]
  >((acc, item) => {
    (['homepage', 'instagram', 'twitter'] as const).forEach((source) => {
      if (item[source]) {
        item[source].forEach((linkItem: SocialLinkStoryblok) => {
          acc.push({
            id: crypto.randomUUID(),
            label: linkItem.linkLabel,
            source,
            url: 'url' in linkItem.link ? (linkItem.link?.url ?? '') : '',
          });
        });
      }
    });
    return acc;
  }, []);
}

const Artist: React.FC<Props> = ({
  blok: {
    achievements,
    achievementsTitle,
    bannerImage,
    byline,
    exclusive,
    highlights,
    highlightsTitle,
    internalId,
    intro,
    name,
    originalArtworks,
    plannedReleases,
    quote,
    relatedArtists,
    social,
    studioGallery,
    thumbnail,
  },
  pageData,
}) => {
  const { t } = useTranslation(['artists', 'common', 'authentication']);
  const router = useRouter();
  const isMobile = useMediaQuery('sm');
  const { user } = useAuth();
  const subscriptionRef = useRef<HTMLAnchorElement>(null);

  const analytics = useMemo(
    () => ({
      context: 'artist page',
      page: ARTIST_PAGE_TYPE,
      'artist name': name,
    }),
    [],
  );

  const artistId = internalId ?? '';
  const { onboarding } = useContext(AccountContext);

  const { authenticateAndSubscribe } = useAuthenticate();
  const artistSubscription = [
    {
      objectId: artistId,
      type: GeneratedSubscriptionType.Artist,
      name: name || '',
      ref: '',
    },
  ];

  const {
    isSubscribedAll: isSubscribed,
    loading: isLoading,
    refetch,
  } = useIsSubscribed(
    artistSubscription.map(({ objectId, type }) => ({
      objectId,
      type,
    })),
  );

  useEffect(() => {
    if (!user && isSubscribed) refetch();
  }, [user]);

  const handleSubscribe = async (buttonLocation?: ButtonLocation) => {
    await authenticateAndSubscribe({
      authScreenThumbnail: thumbnail?.filename || '',
      authScreenTitle: t('authentication:dynamicTitle.artist'),
      subscriptions: artistSubscription,
      analyticsProperties: {
        artist_id: artistId,
        'artist name': name || '',
        source: AuthEntryPoint.Artist,
        'button_location': buttonLocation ?? 'artist page',
      },
      hideSuccessScreen: !onboarding?.isOnboardingStarted, // if onboarding is not started then hide success screen after profile step
      onFlowComplete: () => {
        onboarding?.show({ page: AuthEntryPoint.Artist, id: artistId });
      },
      showOnboarding: !showPhoneStep(user),
      showPhoneStep: showPhoneStep(user),
    });
  };

  const imagesGallery = (studioGallery || [])?.filter(
    (item) => item.component !== 'Video',
  );
  const backgroundImage = bannerImage?.filename
    ? bannerImage
    : (((imagesGallery?.[imagesGallery.length > 1 ? 1 : 0] as GalleryImage)
        ?.image || thumbnail) as AssetStoryblok);

  const artistPlannedReleases = plannedReleases || [];
  const artistProducts = pageData.artistProducts.sort((a, b) => {
    return new Date(b.date).getTime() - new Date(a.date).getTime();
  });

  if (artistPlannedReleases.length === 0 && artistProducts.length === 0) {
    artistPlannedReleases.push({
      releaseType: ['edition'],
      component: 'plannedRelease',
      _uid: 'planned-release-1',
    });
  }

  const insightsArticles = pageData.insights;
  const exclusiveVideoPredicate = (
    item: VideoStoryblok | EventStoryblok,
  ): item is VideoStoryblok =>
    Boolean(item.component === 'Video' && item.vimeo.vimeo_oembed?.response);

  const exclusiveVideos = (exclusive || []).filter(exclusiveVideoPredicate);

  const socialsData = mapArtistsSocials(social || []);

  const tabsOptions: {
    key: string;
    label: string;
    href: string;
  }[] = [
    {
      key: 'works',
      label: t('tabs.works.title'),
      href: updateQueryParameters(router.asPath, [{ key: 'tab', value: null }]),
    },
    {
      key: 'about',
      label: t('tabs.about.title'),
      href: updateQueryParameters(router.asPath, [
        { key: 'tab', value: 'about' },
      ]),
    },
  ];

  if (insightsArticles.length > 0 || exclusiveVideos.length > 0) {
    tabsOptions.push({
      key: 'insights',
      label: t('tabs.insights.title'),
      href: updateQueryParameters(router.asPath, [
        { key: 'tab', value: 'insights' },
      ]),
    });
  }

  const currentTab = useMemo(() => {
    const params = new URLSearchParams(router.query as any);
    const tabParam = params.get('tab') || tabsOptions[0].key;
    const validTab = tabsOptions.find((tab) => tab.key === tabParam);
    return validTab ? tabParam : tabsOptions[0].key;
  }, [router.query, tabsOptions]);

  const tabsProps = {
    current: currentTab,
    urls: tabsOptions.reduce<{
      [key: string]: string;
    }>((acc, curr) => {
      acc[curr.key] = curr.href;
      return acc;
    }, {}),
  };

  useEffect(() => {
    TrackArtistPageView({
      page: ARTIST_PAGE_TYPE,
      artistName: name || '',
      hasUpcomingRelease: pageData.hasUpcomingProducts,
      collaborationsWithArtist: pageData.collaborationsWithArtist,
      hasUpcomingProductPage: pageData.hasUpcomingProductPage,
      currentTab,
    });
  }, []);

  return (
    <AnalyticsObjectProvider name="artist page" properties={analytics}>
      {() => (
        <div>
          <StickyHeader ctaRef={subscriptionRef}>
            <div className="flex w-full items-center space-x-3">
              <div className="h-12 w-12 shrink-0 rounded-sm">
                <Image
                  alt={thumbnail?.alt || name || ''}
                  focus={thumbnail?.focus}
                  shape="rounded"
                  src={thumbnail ? thumbnail.filename : PLACE_HOLDER_IMAGE}
                  width={48}
                />
              </div>
              <div className="flex-1 overflow-hidden">
                <p className="truncate">{name}</p>
                <p className="truncate text-neutral-4">
                  {pageData.collaborationsWithArtist
                    ? t('common:collaborations', {
                        count: pageData.collaborationsWithArtist,
                      })
                    : t('artistCollaborationUpcoming')}
                </p>
              </div>
              <div className="flex shrink-0 items-center justify-center space-x-3">
                <Button
                  disabled={isSubscribed}
                  hideLabel={isMobile}
                  icon={
                    isSubscribed
                      ? 'ic_notification_active'
                      : 'ic_notification_outline'
                  }
                  label={
                    isSubscribed
                      ? t('subscriptionButtonSuccess')
                      : t('subscriptionButton')
                  }
                  loading={isLoading}
                  onClick={() => handleSubscribe('artist page sticky')}
                  size={isMobile ? 'sm' : 'md'}
                  variant={isSubscribed ? 'positive' : 'primary'}
                />
              </div>
            </div>
          </StickyHeader>
          <main className="my-9 flex flex-col space-y-9 md:my-12 md:space-y-12">
            <PageSection>
              <div className="flex flex-col gap-y-6">
                <ArtistHero
                  hero={backgroundImage}
                  name={name!}
                  thumbnail={thumbnail}
                />

                <div className="mx-auto flex w-full max-w-sm items-center justify-center">
                  <Button
                    disabled={isSubscribed}
                    icon={
                      isSubscribed
                        ? 'ic_notification_active'
                        : 'ic_notification_outline'
                    }
                    label={
                      isSubscribed
                        ? t('subscriptionButtonSuccess')
                        : t('subscriptionButton')
                    }
                    loading={isLoading}
                    onClick={() => handleSubscribe('artist page')}
                    size="sm"
                    variant={isSubscribed ? 'positive' : 'tertiary'}
                  />
                  <ScrollAnchor ref={subscriptionRef} id="subscriptionRef" />
                </div>
              </div>
            </PageSection>

            <PageSection
              className="z-20 my-6 w-full bg-white sm:mb-9 md:mb-12"
              fullWidth
            >
              <div className="relative flex w-full overflow-x-scroll px-website no-scrollbar">
                <Tabs activeTab={tabsProps.current} tabs={tabsOptions} />
              </div>
              <div className="absolute bottom-0 left-0 right-0 h-px bg-divider/20" />
            </PageSection>

            <div className="relative">
              {tabsOptions.map((tab) => (
                <motion.div
                  key={tab.key}
                  animate={
                    tab.key === tabsProps.current ? 'active' : 'inactive'
                  }
                  aria-labelledby={`tab-${tab.key}`}
                  className="w-full"
                  id={`panel-${tab.key}`}
                  initial="inactive"
                  role="tabpanel"
                  tabIndex={0}
                  variants={{
                    active: {
                      opacity: 1,
                      display: 'block',
                      y: 0,
                      transition: {
                        duration: 0.3,
                      },
                    },
                    inactive: {
                      opacity: 0,
                      display: 'none',
                      y: 120,
                      transition: {
                        duration: 0.3,
                      },
                    },
                  }}
                >
                  {tab.key === 'works' && (
                    <ArtistProductsTab
                      analytics={analytics}
                      artist={name}
                      draws={pageData.draws}
                      image={
                        artistPlannedReleases.length > 0
                          ? backgroundImage
                          : undefined
                      }
                      isLoading={isLoading}
                      isSubscribed={isSubscribed}
                      onSubscribe={() =>
                        handleSubscribe('artist page upcoming')
                      }
                      products={artistProducts}
                      upcoming={artistPlannedReleases}
                    />
                  )}
                  {tab.key === 'about' && (
                    <ArtistAboutTab
                      achievements={{
                        label: achievementsTitle,
                        content: achievements,
                      }}
                      headline={byline}
                      highlights={{
                        label: highlightsTitle,
                        content: highlights,
                      }}
                      intro={{ label: 'bioTitle', content: intro }}
                      name={name || ''}
                      originalArtworks={originalArtworks}
                      quote={quote}
                      social={socialsData}
                      studioGallery={studioGallery}
                      text={pageData.practiceOverview}
                    />
                  )}
                  {tab.key === 'insights' && (
                    <ArtistInsightsTab
                      insights={insightsArticles}
                      videos={exclusiveVideos}
                    />
                  )}
                </motion.div>
              ))}
            </div>

            <PageSection>
              <Divider />
            </PageSection>

            <PageSection>
              <RelatedArtists
                artists={relatedArtists}
                onArtistClick={(index, uuid, name, link) => {
                  TrackArtistClicked({
                    page: ARTIST_PAGE_TYPE,
                    context: 'other artists',
                    id: uuid,
                    artistName: name,
                    artistUrl: link,
                    position: index,
                  });
                }}
                onViewAllArtistsClick={() => {
                  TrackLinkClicked({
                    page: ARTIST_PAGE_TYPE,
                    context: 'other artists see all',
                    title: 'see all',
                    url: pathFor({ page: 'ARTISTS_INDEX' }),
                    isExternal: false,
                  });
                }}
                title={t('discoverOtherArtists')}
              />
            </PageSection>
          </main>
        </div>
      )}
    </AnalyticsObjectProvider>
  );
};

export default Artist;
